/**
 * ScorerEncounterListPage.jsx
 * Redesigned scorer workflow:
 *   Step 0: Select Sport
 *   Step 1: Search / select a Team
 *   Step 2: (auto-skipped if only 1 tie) Pick the specific Tie
 *   Step 3: Pick a Match within that Tie
 *   → Navigates to ScorerSportPage with { focusTie: idx }
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, RefreshCw, ChevronRight, ChevronLeft, Clock, Search, CheckCircle2, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

import SportIcon, { SPORT_LABELS, getSportEmoji as SPORT_ICON_FN } from '@/components/SportIcon';

// Legacy emoji map kept for non-JSX usage
const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
  cricket:'🏏', football:'⚽', default:'🏅',
};

const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
  : '';

// ── Breadcrumb bar ──────────────────────────────────────────────────────────
function Breadcrumb({ sport, team, enc, onSport, onTeam, onEnc }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-[#8a93a3] mb-5 flex-wrap">
      <button onClick={onSport} className="hover:text-[#f5c518] transition-colors font-medium">
        <SportIcon sport={sport} size={54} /> {SPORT_LABELS[sport] || sport}
      </button>
      {team && (
        <>
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
          <button onClick={onTeam} className="hover:text-[#f5c518] transition-colors font-medium">{team.name}</button>
        </>
      )}
      {enc && (
        <>
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
          <span className="text-[#f0f2f7] font-medium">
            {enc.home_team?.name || enc.home_seed} vs {enc.away_team?.name || enc.away_seed}
          </span>
        </>
      )}
    </div>
  );
}

export default function ScorerEncounterListPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token = () => localStorage.getItem('btccp_access_token');
  const navigate = useNavigate();

  const [encounters, setEncounters] = useState([]);
  const [scorerSports, setScorerSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualTournId, setManualTournId] = useState(null); // admin-selected tournament override

  // Wizard state
  const [step, setStep] = useState(0);        // 0=sport 1=team 2=tie 3=match
  const [selSport, setSelSport] = useState(null);
  const [selTeamId, setSelTeamId] = useState(null);
  const [selEncId, setSelEncId] = useState(null);
  const [teamSearch, setTeamSearch] = useState('');

  // ── Data ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [encRes, usersRes] = await Promise.all([
        fetch(`/api/orgs/${orgSlug}/scorer/encounters`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`/api/orgs/${orgSlug}/users`, { headers: { Authorization: `Bearer ${token()}` } }),
      ]);
      if (encRes.ok) setEncounters(await encRes.json());
      if (usersRes.ok) {
        const users = await usersRes.json();
        const me = users.find(u => u.phone === user?.phone);
        setScorerSports(me?.scorer_sports || []);
      }
    } catch (_) { if (!silent) toast.error('Failed to load matches'); }
    finally { if (!silent) setLoading(false); }
  }, [orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
    const iv = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  // ── Derived ───────────────────────────────────────────────────────────────
  // All tournaments that have encounters (for the selector)
  const allTournaments = useMemo(() => {
    const map = {};
    encounters.forEach(e => {
      if (!e.tournament_id) return;
      if (!map[e.tournament_id]) map[e.tournament_id] = { id: e.tournament_id, name: e.tournament_name || e.tournament_id };
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [encounters]);

  // Determine "current" tournament — manual override > auto-pick
  const currentTournId = useMemo(() => {
    if (manualTournId) return manualTournId;
    if (!encounters.length) return null;
    const byTourn = {};
    encounters.forEach(e => {
      if (!e.tournament_id) return;
      if (!byTourn[e.tournament_id]) byTourn[e.tournament_id] = [];
      byTourn[e.tournament_id].push(e);
    });
    const ids = Object.keys(byTourn);
    // Prefer tournaments that still have pending/live encounters
    const active = ids.filter(id => byTourn[id].some(e => e.status !== 'completed'));
    const pool = active.length ? active : ids;
    return pool.sort((a, b) => {
      const maxA = Math.max(...byTourn[a].map(e => new Date(e.scheduled_at || 0).getTime()));
      const maxB = Math.max(...byTourn[b].map(e => new Date(e.scheduled_at || 0).getTime()));
      return maxB - maxA;
    })[0] || null;
  }, [encounters, manualTournId]);

  // Tournament name to display
  const currentTournName = useMemo(() => {
    const enc = encounters.find(e => e.tournament_id === currentTournId);
    return enc?.tournament_name || '';
  }, [encounters, currentTournId]);

  // All encounters scoped to the current tournament
  const currentEncounters = useMemo(() =>
    currentTournId ? encounters.filter(e => e.tournament_id === currentTournId) : encounters,
  [encounters, currentTournId]);

  const availableSports = useMemo(() => [...new Set(
    currentEncounters.flatMap(e => (e.sport_results || []).map(sr => sr.sport_code))
  )].filter(sc => scorerSports.length === 0 || scorerSports.includes(sc))
    .sort((a, b) => (SPORT_LABELS[a] || a).localeCompare(SPORT_LABELS[b] || b)),
  [currentEncounters, scorerSports]);

  const sportEncounters = useMemo(() => selSport
    ? currentEncounters
        .filter(e => {
          if (!e.sport_results?.some(sr => sr.sport_code === selSport)) return false;
          // Group-stage encounters always included.
          // Knockout encounters only included once BOTH real team IDs are assigned
          // (prevents TBD semifinal/final seeds from appearing prematurely).
          const isGroup = !e.round_type || e.round_type === 'group';
          const bothAssigned = e.home_team_id && e.away_team_id;
          return isGroup || bothAssigned;
        })
        .sort((a, b) => (a.scheduled_at || 'Z').localeCompare(b.scheduled_at || 'Z'))
    : [], [currentEncounters, selSport]);

  // All unique teams playing in this sport
  // Falls back to seed label (e.g. "A1") when no real team is assigned yet
  const teamsInSport = useMemo(() => {
    const map = new Map();
    sportEncounters.forEach(enc => {
      // Home side — use team_id if assigned, else seed string as key
      const homeKey  = enc.home_team_id || enc.home_seed;
      const homeName = enc.home_team?.name && enc.home_team.name !== 'TBD'
        ? enc.home_team.name : (enc.home_seed || 'Home');
      if (homeKey) map.set(homeKey, {
        id:     homeKey,
        name:   homeName,
        color:  enc.home_team?.color || '#3b82f6',
        logo:   enc.home_team?.logo_url,
        isSeed: !enc.home_team_id,
      });
      // Away side
      const awayKey  = enc.away_team_id || enc.away_seed;
      const awayName = enc.away_team?.name && enc.away_team.name !== 'TBD'
        ? enc.away_team.name : (enc.away_seed || 'Away');
      if (awayKey) map.set(awayKey, {
        id:     awayKey,
        name:   awayName,
        color:  enc.away_team?.color || '#ef4444',
        logo:   enc.away_team?.logo_url,
        isSeed: !enc.away_team_id,
      });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [sportEncounters]);

  const filteredTeams = useMemo(() => teamSearch
    ? teamsInSport.filter(t => t.name.toLowerCase().includes(teamSearch.toLowerCase()))
    : teamsInSport, [teamsInSport, teamSearch]);

  // Filter encounters for selected team — works for both real team IDs and seed labels
  const matchesByTeam = (enc, teamKey) =>
    enc.home_team_id === teamKey || enc.away_team_id === teamKey ||
    (!enc.home_team_id && enc.home_seed === teamKey) ||
    (!enc.away_team_id && enc.away_seed === teamKey);

  const teamEncounters = useMemo(() => selTeamId
    ? sportEncounters.filter(enc => matchesByTeam(enc, selTeamId))
    : [], [sportEncounters, selTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selEnc = useMemo(() => encounters.find(e => e.id === selEncId) || null, [encounters, selEncId]);
  const selSR = useMemo(() => selEnc?.sport_results?.find(sr => sr.sport_code === selSport) || null, [selEnc, selSport]);
  const selTeam = useMemo(() => teamsInSport.find(t => t.id === selTeamId) || null, [teamsInSport, selTeamId]);

  const liveCount = currentEncounters.filter(e => e.status === 'in_progress').length;

  // Global match offset for display numbering
  const globalMatchStart = useMemo(() => {
    let offset = 1;
    for (const sc of availableSports) {
      if (sc === selSport) break;
      offset += currentEncounters.reduce((s, e) => {
        const sr = e.sport_results?.find(r => r.sport_code === sc);
        return s + (sr?.matches?.length || 0);
      }, 0);
    }
    return offset;
  }, [availableSports, selSport, currentEncounters]);

  const encIndexInSport = sportEncounters.findIndex(e => e.id === selEncId);
  const matchesBeforeThisEnc = sportEncounters.slice(0, encIndexInSport).reduce((s, e) => {
    const sr = e.sport_results?.find(r => r.sport_code === selSport);
    return s + (sr?.matches?.length || 0);
  }, 0);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSportSelect = (sc) => {
    setSelSport(sc);
    setSelTeamId(null);
    setSelEncId(null);
    setTeamSearch('');
    setStep(1);
  };

  const handleTeamSelect = (team) => {
    setSelTeamId(team.id);
    setTeamSearch('');
    const encs = sportEncounters.filter(enc => matchesByTeam(enc, team.id));
    if (encs.length === 1) {
      setSelEncId(encs[0].id);
      setStep(3); // skip Step 2 — go straight to match list
    } else {
      setStep(2);
    }
  };

  const handleEncSelect = (encId) => {
    setSelEncId(encId);
    setStep(3);
  };

  const goBackTo = (targetStep) => {
    setStep(targetStep);
    if (targetStep <= 0) { setSelSport(null); setSelTeamId(null); setSelEncId(null); setTeamSearch(''); }
    if (targetStep <= 1) { setSelTeamId(null); setSelEncId(null); setTeamSearch(''); }
    if (targetStep <= 2) { setSelEncId(null); }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) return (
    <div className="p-4 space-y-3">
      {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 bg-[#1d2230] rounded-xl" />)}
    </div>
  );

  // ── STEP 0 — Sport Selection ──────────────────────────────────────────────
  if (step === 0) return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#f0f2f7]">Scorer Portal</h1>
          <p className="text-xs text-[#8a93a3] mt-0.5">Select a sport to begin</p>
        </div>
        <Button variant="ghost" size="icon" onClick={fetchData} className="text-[#8a93a3]">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Multiple tournaments selector */}
      {currentTournName && (
        <div className="mb-4 px-3 py-2 bg-[rgba(245,197,24,0.06)] border border-[rgba(245,197,24,0.2)] rounded-xl flex items-center gap-2">
          <Trophy className="h-3.5 w-3.5 text-[#f5c518] flex-shrink-0" />
          <span className="text-[#f5c518] font-semibold text-sm truncate">{currentTournName}</span>
        </div>
      )}

      {liveCount > 0 && (
        <div className="mb-4 px-3 py-2 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.3)] rounded-xl flex items-center gap-2">
          <span className="w-2 h-2 bg-[#ef4444] rounded-full animate-pulse flex-shrink-0" />
          <span className="text-[#ef4444] font-semibold text-sm">
            {liveCount} match{liveCount > 1 ? 'es' : ''} in progress
          </span>
        </div>
      )}

      {availableSports.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-10 text-center">
            <Target className="h-10 w-10 text-[#8a93a3] mx-auto mb-3" />
            <p className="text-[#f0f2f7] font-semibold">No matches available</p>
            <p className="text-sm text-[#8a93a3] mt-1">Check with the tournament admin</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {availableSports.map(sc => {
            const total = currentEncounters.reduce((s, e) => {
              const sr = e.sport_results?.find(r => r.sport_code === sc);
              return s + (sr?.matches?.length || 0);
            }, 0);
            const done = currentEncounters.reduce((s, e) => {
              const sr = e.sport_results?.find(r => r.sport_code === sc);
              return s + (sr?.matches?.filter(m => m.winner).length || 0);
            }, 0);
            const pct = total > 0 ? (done / total) * 100 : 0;
            return (
              <Card key={sc} data-testid={`sport-card-${sc}`}
                onClick={() => handleSportSelect(sc)}
                className="bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.5)] cursor-pointer hover:-translate-y-0.5 transition-all">
                <CardContent className="p-5 text-center">
                  <div className="text-4xl mb-2"><SportIcon sport={sc} size={54} /></div>
                  <p className="font-black text-[#f0f2f7] text-base">{SPORT_LABELS[sc] || sc}</p>
                  <p className="text-xs text-[#8a93a3] mt-1">{done}/{total} done</p>
                  {done < total && (
                    <div className="mt-2 h-1.5 bg-[#1d2230] rounded-full overflow-hidden">
                      <div className="h-full bg-[#f5c518] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                  {done === total && total > 0 && (
                    <div className="mt-2 flex items-center justify-center gap-1 text-[#22c55e] text-[10px] font-semibold">
                      <CheckCircle2 className="h-3 w-3" /> Complete
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── STEP 1 — Team Search ──────────────────────────────────────────────────
  if (step === 1) return (
    <div className="p-4 sm:p-6">
      <button onClick={() => goBackTo(0)}
        className="flex items-center gap-1 text-[#8a93a3] hover:text-[#f0f2f7] mb-4 text-sm">
        <ChevronLeft className="h-4 w-4" /> Back to Sports
      </button>

      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl"><SportIcon sport={selSport} size={42} /></span>
        <div>
          <h1 className="text-xl font-bold text-[#f0f2f7]">{SPORT_LABELS[selSport] || selSport}</h1>
          <p className="text-xs text-[#8a93a3]">Select a team to find their tie</p>
        </div>
      </div>

      {/* Search box */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
        <Input
          data-testid="team-search-input"
          value={teamSearch}
          onChange={e => setTeamSearch(e.target.value)}
          placeholder="Search team name..."
          autoFocus
          className="pl-9 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#4a5568] focus:border-[#f5c518] focus-visible:ring-0"
        />
      </div>

      {filteredTeams.length === 0 && (
        <p className="text-center text-[#8a93a3] text-sm py-10">No teams found</p>
      )}

      <div className="space-y-2">
        {filteredTeams.map(team => {
          const encs = sportEncounters.filter(enc =>
            enc.home_team_id === team.id || enc.away_team_id === team.id
          );
          const doneEncs = encs.filter(enc => {
            const sr = enc.sport_results?.find(r => r.sport_code === selSport);
            return !!sr?.sport_winner;
          }).length;
          const pendingEncs = encs.length - doneEncs;
          const liveEncs = encs.filter(e => e.status === 'in_progress').length;

          return (
            <Card key={team.id} data-testid={`team-card-${team.id}`}
              onClick={() => handleTeamSelect(team)}
              className="bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.4)] cursor-pointer hover:-translate-y-0.5 transition-all">
              <CardContent className="p-4 flex items-center gap-3">
                {/* Team avatar */}
                <div className="h-11 w-11 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-white text-lg overflow-hidden"
                  style={{ backgroundColor: team.color }}>
                  {team.logo?.startsWith('data:')
                    ? <img src={team.logo} className="h-full w-full object-cover" alt="" />
                    : team.name[0]}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#f0f2f7] text-base">{team.name}</p>
                  <p className="text-xs text-[#8a93a3]">
                    {encs.length} tie{encs.length !== 1 ? 's' : ''} · {doneEncs} completed
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {liveEncs > 0 && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-[#ef4444]">
                      <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse" />
                      LIVE
                    </span>
                  )}
                  {pendingEncs > 0 && (
                    <Badge className="bg-[rgba(245,197,24,0.1)] text-[#f5c518] border-[rgba(245,197,24,0.3)] text-[10px] px-1.5">
                      {pendingEncs} pending
                    </Badge>
                  )}
                  {pendingEncs === 0 && doneEncs === encs.length && encs.length > 0 && (
                    <CheckCircle2 className="h-4 w-4 text-[#22c55e]" />
                  )}
                  <ChevronRight className="h-5 w-5 text-[#8a93a3]" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  // ── STEP 2 — Pick Tie for this Team (only if multiple encounters) ─────────
  if (step === 2) return (
    <div className="p-4 sm:p-6">
      <button onClick={() => goBackTo(1)}
        className="flex items-center gap-1 text-[#8a93a3] hover:text-[#f0f2f7] mb-4 text-sm">
        <ChevronLeft className="h-4 w-4" /> Back to Teams
      </button>

      {selTeam && (
        <Breadcrumb sport={selSport} team={selTeam}
          onSport={() => goBackTo(0)} onTeam={() => goBackTo(1)} />
      )}

      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-white overflow-hidden"
          style={{ backgroundColor: selTeam?.color }}>
          {selTeam?.logo?.startsWith('data:')
            ? <img src={selTeam.logo} className="h-full w-full object-cover" alt="" />
            : selTeam?.name[0]}
        </div>
        <div>
          <h1 className="text-xl font-bold text-[#f0f2f7]">{selTeam?.name}</h1>
          <p className="text-xs text-[#8a93a3]">Select the tie you want to score</p>
        </div>
      </div>

      {/* Group encounters by stage: Group Stage → Knockout */}
      {(() => {
        const groupEncs = teamEncounters.filter(e => !e.round_type || e.round_type === 'group');
        const koEncs    = teamEncounters.filter(e => ['sf','final','3rd'].includes(e.round_type));

        const ROUND_LABEL = { sf: 'SEMI-FINAL', final: 'FINAL', '3rd': '3RD PLACE' };
        const ROUND_COLOR = {
          sf:    { border: 'rgba(245,197,24,0.45)', bg: 'rgba(245,197,24,0.05)', text: '#f5c518' },
          final: { border: 'rgba(251,146,60,0.55)', bg: 'rgba(251,146,60,0.06)', text: '#fb923c' },
          '3rd': { border: 'rgba(139,92,246,0.4)',  bg: 'rgba(139,92,246,0.05)', text: '#8b5cf6' },
        };

        const renderEncCard = (enc) => {
          const sr = enc.sport_results?.find(r => r.sport_code === selSport);
          const done = (sr?.matches || []).filter(m => m.winner).length;
          const total = (sr?.matches || []).length;
          const isDone = !!sr?.sport_winner;
          const isLive = enc.status === 'in_progress';
          const isKO   = ['sf','final','3rd'].includes(enc.round_type);
          const roundCol = isKO ? (ROUND_COLOR[enc.round_type] || ROUND_COLOR.sf) : null;
          const hl = enc.home_team?.name || enc.home_seed || 'Home';
          const al = enc.away_team?.name || enc.away_seed || 'Away';
          return (
            <Card key={enc.id} data-testid={`enc-card-${enc.id}`}
              onClick={() => handleEncSelect(enc.id)}
              style={isKO && roundCol ? { borderColor: roundCol.border, backgroundColor: roundCol.bg } : {}}
              className={`border cursor-pointer hover:-translate-y-0.5 transition-all ${
                !isKO ? (
                  isDone ? 'bg-[#161a26] border-[rgba(34,197,94,0.3)]' :
                  isLive ? 'bg-[#161a26] border-[rgba(239,68,68,0.35)]' :
                           'bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.35)]'
                ) : ''
              }`}>
              <CardContent className="p-4">
                {/* Round badge for KO */}
                {isKO && (
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-[10px] font-black tracking-widest px-2 py-0.5 rounded-full"
                      style={{ color: roundCol?.text, backgroundColor: `${roundCol?.border}30`, border: `1px solid ${roundCol?.border}` }}>
                      {ROUND_LABEL[enc.round_type] || enc.round_type.toUpperCase()}
                    </span>
                    {enc.match_label && <span className="text-[10px] font-mono text-[#8a93a3]">{enc.match_label}</span>}
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2 text-xs">
                  {isLive && (
                    <span className="flex items-center gap-1 text-[#ef4444] font-bold">
                      <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse" />LIVE
                    </span>
                  )}
                  {isDone && <span className="text-[#22c55e] font-semibold">✓ Done</span>}
                  {!isKO && enc.match_label && <span className="font-mono text-[#8a93a3]">{enc.match_label}</span>}
                  {enc.scheduled_at && (
                    <span className="text-[#8a93a3] flex items-center gap-1 ml-auto">
                      <Clock className="h-3 w-3" />{fmtTime(enc.scheduled_at)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`flex-1 font-bold text-sm ${sr?.sport_winner === 'home' ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>{hl}</span>
                  <span className={`font-black text-base tabular-nums ${isDone||isLive?'text-[#f5c518]':'text-[#8a93a3]'}`}>
                    {isDone||isLive ? `${sr?.home_match_wins??0}–${sr?.away_match_wins??0}` : 'vs'}
                  </span>
                  <span className={`flex-1 font-bold text-sm text-right ${sr?.sport_winner === 'away' ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>{al}</span>
                  <ChevronRight className="h-4 w-4 text-[#8a93a3] flex-shrink-0 ml-1" />
                </div>
                <div className="mt-2.5 h-1 bg-[#1d2230] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${total>0?(done/total)*100:0}%`, backgroundColor: isKO?(roundCol?.text||'#f5c518'):'#f5c518' }}/>
                </div>
                <p className="text-[10px] text-[#8a93a3] mt-1">{done}/{total} matches scored</p>
              </CardContent>
            </Card>
          );
        };

        return (
          <div className="space-y-4">
            {groupEncs.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-[#8a93a3] uppercase tracking-widest px-1">Group Stage</p>
                {groupEncs.map(renderEncCard)}
              </div>
            )}
            {koEncs.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-[10px] font-bold text-[#f5c518] uppercase tracking-widest px-1">Knockout</p>
                  <div className="flex-1 h-px bg-[rgba(245,197,24,0.2)] rounded-full"/>
                </div>
                {koEncs.map(renderEncCard)}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );

  // ── STEP 3 — Match List within Tie ────────────────────────────────────────
  if (step === 3 && selEnc && selSR) {
    const hl = selEnc.home_team?.name || selEnc.home_seed || 'Home';
    const al = selEnc.away_team?.name || selEnc.away_seed || 'Away';
    const hc = selEnc.home_team?.color || '#3b82f6';
    const ac = selEnc.away_team?.color || '#ef4444';
    const tieScore = `${selSR.home_match_wins ?? 0}–${selSR.away_match_wins ?? 0}`;
    const tieWinner = selSR.sport_winner;
    const totalMatches = selSR.matches?.length || 0;
    const doneMatches = (selSR.matches || []).filter(m => m.winner).length;

    return (
      <div className="p-4 sm:p-6">
        <button onClick={() => {
          if (teamEncounters.length > 1) goBackTo(2);
          else goBackTo(1);
        }} className="flex items-center gap-1 text-[#8a93a3] hover:text-[#f0f2f7] mb-4 text-sm">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {selTeam && (
          <Breadcrumb sport={selSport} team={selTeam} enc={selEnc}
            onSport={() => goBackTo(0)} onTeam={() => goBackTo(1)} onEnc={() => goBackTo(2)} />
        )}

        {/* KO round banner */}
        {['sf','final','3rd'].includes(selEnc.round_type) && (() => {
          const KO_LABELS = { sf:'SEMI-FINAL', final:'FINAL', '3rd':'3RD PLACE' };
          const KO_COLORS = {
            sf:    { bg:'rgba(245,197,24,0.08)', border:'rgba(245,197,24,0.4)', text:'#f5c518' },
            final: { bg:'rgba(251,146,60,0.08)', border:'rgba(251,146,60,0.5)', text:'#fb923c' },
            '3rd': { bg:'rgba(139,92,246,0.08)', border:'rgba(139,92,246,0.4)', text:'#8b5cf6' },
          };
          const col = KO_COLORS[selEnc.round_type] || KO_COLORS.sf;
          return (
            <div className="mb-4 px-3 py-2.5 rounded-xl flex items-center gap-3"
              style={{ backgroundColor: col.bg, border: `1px solid ${col.border}` }}>
              <span className="text-xl">🏆</span>
              <div>
                <p className="text-xs font-black tracking-widest" style={{ color: col.text }}>
                  {KO_LABELS[selEnc.round_type]}
                </p>
                <p className="text-[10px] text-[#8a93a3]">
                  {selEnc.match_label || 'Knockout Match'} · Score carefully, this counts for the title
                </p>
              </div>
            </div>
          );
        })()}

        {/* Tie header card */}
        <div className={`rounded-2xl border p-4 mb-5 ${
          tieWinner ? 'border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.04)]' :
          selEnc.status === 'in_progress' ? 'border-[rgba(239,68,68,0.35)] bg-[rgba(239,68,68,0.03)]' :
          'border-[#252a37] bg-[#161a26]'
        }`}>
          <div className="flex items-center gap-2 text-xs mb-3">
            <span className="text-2xl"><SportIcon sport={selSport} size={42} /></span>
            <div>
              <p className="font-bold text-[#f0f2f7] text-sm">{SPORT_LABELS[selSport] || selSport}</p>
              {selEnc.match_label && <span className="font-mono text-[#8a93a3]">{selEnc.match_label}</span>}
            </div>
            {selEnc.status === 'in_progress' && (
              <span className="ml-auto flex items-center gap-1 text-[#ef4444] font-bold text-xs">
                <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse" />LIVE
              </span>
            )}
            {tieWinner && <CheckCircle2 className="ml-auto h-4 w-4 text-[#22c55e]" />}
          </div>

          <div className="flex items-center gap-3">
            {/* Home */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="h-9 w-9 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-white text-sm overflow-hidden"
                style={{ backgroundColor: hc }}>
                {selEnc.home_team?.logo_url?.startsWith('data:')
                  ? <img src={selEnc.home_team.logo_url} className="h-full w-full object-cover" alt="" />
                  : hl[0]}
              </div>
              <span className={`font-bold text-sm truncate ${tieWinner === 'home' ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>{hl}</span>
            </div>
            {/* Score */}
            <div className="text-center flex-shrink-0">
              <span className="font-black text-xl text-[#f5c518] tabular-nums">{tieScore}</span>
              <p className="text-[10px] text-[#8a93a3] mt-0.5">{doneMatches}/{totalMatches} scored</p>
            </div>
            {/* Away */}
            <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
              <span className={`font-bold text-sm truncate text-right ${tieWinner === 'away' ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>{al}</span>
              <div className="h-9 w-9 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-white text-sm overflow-hidden"
                style={{ backgroundColor: ac }}>
                {selEnc.away_team?.logo_url?.startsWith('data:')
                  ? <img src={selEnc.away_team.logo_url} className="h-full w-full object-cover" alt="" />
                  : al[0]}
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-[#8a93a3] mb-3 font-medium uppercase tracking-wider">Select a match to score</p>

        {/* Individual match cards */}
        <div className="space-y-2">
          {(selSR.matches || []).map((m, idx) => {
            const globalNum = globalMatchStart + matchesBeforeThisEnc + idx;
            const isDone = !!m.winner;
            const isPlat = m.slot?.[0] === 'P' || m.tier === 'platinum';
            const slotColor = isPlat ? '#f5c518' : '#3b82f6';
            const hp = m.home_player || m.home_players?.[0] || hl;
            const ap = m.away_player || m.away_players?.[0] || al;
            const isResultSport = ['chess', 'carrom'].includes(selSport);

            return (
              <Card key={idx}
                data-testid={`match-card-${idx}`}
                onClick={() => navigate(`/score/enc/${selEnc.id}/sport/${selSport}`, { state: { focusTie: idx } })}
                className={`border cursor-pointer transition-all hover:-translate-y-0.5 ${
                  isDone ? 'bg-[#0f1219] border-[rgba(34,197,94,0.25)]' :
                  isPlat ? 'bg-[#161a26] border-[rgba(245,197,24,0.4)] ring-1 ring-[rgba(245,197,24,0.12)]' :
                           'bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.35)]'
                }`}>
                <CardContent className="p-3.5 flex items-center gap-3">
                  {/* Global match number badge */}
                  <div className={`h-12 w-12 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-lg border-2 ${
                    isDone ? 'bg-[rgba(34,197,94,0.08)] border-[rgba(34,197,94,0.3)] text-[#22c55e]' :
                    isPlat ? 'bg-[rgba(245,197,24,0.08)] border-[rgba(245,197,24,0.4)] text-[#f5c518]' :
                             'bg-[#1d2230] border-[#252a37] text-[#8a93a3]'
                  }`}>
                    {isDone ? <CheckCircle2 className="h-5 w-5" /> : globalNum}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Slot badge + labels */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: `${slotColor}20`, color: slotColor }}>
                        {m.slot || m.label || `M${idx + 1}`}
                      </span>
                      <span className="text-[10px] text-[#8a93a3] bg-[#1d2230] px-1.5 py-0.5 rounded border border-[#252a37]">
                        {m.type === 'doubles' ? 'Doubles' : 'Singles'}
                      </span>
                      {isPlat && <span className="text-[10px] font-bold text-[#f5c518]">★ Platinum</span>}
                    </div>

                    {/* Players */}
                    <p className="text-sm font-semibold text-[#f0f2f7]">
                      <span>{hp}</span>
                      <span className="text-[#8a93a3] mx-1.5 font-normal">vs</span>
                      <span>{ap}</span>
                    </p>

                    {/* Result summary */}
                    {isDone && (
                      <div className="mt-0.5">
                        {isResultSport ? (
                          m.winner === 'draw'
                            ? <span className="text-[10px] text-[#f5c518] font-semibold">🤝 Draw — 1 pt each</span>
                            : <span className="text-[10px] text-[#22c55e] font-semibold">
                                🏆 {m.winner === 'home' ? hp : ap} wins · +2 pts
                              </span>
                        ) : (
                          <span className="text-[10px] text-[#8a93a3]">
                            <span className={m.winner === 'home' ? 'text-[#22c55e] font-bold' : ''}>{m.home_score ?? 0}</span>
                            <span className="mx-1">–</span>
                            <span className={m.winner === 'away' ? 'text-[#22c55e] font-bold' : ''}>{m.away_score ?? 0}</span>
                          </span>
                        )}
                      </div>
                    )}

                    {!isDone && (
                      <p className="text-[10px] text-[#8a93a3] mt-0.5">Tap to score this match</p>
                    )}
                  </div>

                  <ChevronRight className="h-5 w-5 text-[#8a93a3] flex-shrink-0" />
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Golden game card — shown when TT all matches done and tied */}
        {selSport === 'table_tennis' && (() => {
          const matches = selSR?.matches || [];
          const allDone = matches.length > 0 && matches.every(m => m.winner);
          const hw = matches.filter(m => ['home','bye_home'].includes(m.winner)).length;
          const aw = matches.filter(m => ['away','bye_away'].includes(m.winner)).length;
          if (!allDone || hw !== aw) return null;
          const bonusWinner = selSR?.bonus_game?.winner;
          const bonusWinnerName = bonusWinner === 'home'
            ? (selEnc?.home_team?.name || selEnc?.home_seed)
            : (selEnc?.away_team?.name || selEnc?.away_seed);
          return (
            <Card key="golden-game" data-testid="golden-game-card"
              onClick={() => navigate(`/score/enc/${selEnc.id}/sport/table_tennis`, { state: { goldenGame: true } })}
              className={`border cursor-pointer transition-all hover:-translate-y-0.5 ${
                bonusWinner
                  ? 'bg-[#0f1219] border-[rgba(34,197,94,0.25)]'
                  : 'bg-[rgba(245,197,24,0.04)] border-[rgba(245,197,24,0.5)] ring-1 ring-[rgba(245,197,24,0.1)]'
              }`}>
              <CardContent className="p-3.5 flex items-center gap-3">
                <div className={`h-12 w-12 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-lg border-2 ${
                  bonusWinner
                    ? 'bg-[rgba(34,197,94,0.08)] border-[rgba(34,197,94,0.3)] text-[#22c55e]'
                    : 'bg-[rgba(245,197,24,0.12)] border-[rgba(245,197,24,0.5)] text-[#f5c518]'
                }`}>
                  {bonusWinner ? <CheckCircle2 className="h-5 w-5" /> : '⚡'}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[rgba(245,197,24,0.15)] text-[#f5c518] border border-[rgba(245,197,24,0.3)]">
                      GOLDEN GAME
                    </span>
                    <span className="text-[10px] text-[#8a93a3] bg-[#1d2230] px-1.5 py-0.5 rounded border border-[#252a37]">
                      No Points
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-[#f0f2f7]">
                    {bonusWinner
                      ? `⚡ ${bonusWinnerName} wins tie (golden game)`
                      : `${hw}–${aw} tied · Play bonus deciding game`}
                  </p>
                  {!bonusWinner && <p className="text-[10px] text-[#8a93a3] mt-0.5">Tap to score the deciding game</p>}
                </div>
                <ChevronRight className="h-5 w-5 text-[#8a93a3] flex-shrink-0" />
              </CardContent>
            </Card>
          );
        })()}

        {/* Tie complete banner */}
        {tieWinner && (
          <div className="mt-4 px-4 py-3 bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.25)] rounded-xl text-center">
            <p className="text-[#22c55e] font-bold text-sm">
              ✓ Tie complete — {tieWinner === 'home' ? hl : tieWinner === 'away' ? al : 'Draw'} wins {tieScore}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Fallback skeleton
  return (
    <div className="p-4 space-y-3">
      {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 bg-[#1d2230] rounded-xl" />)}
    </div>
  );
}
